import { Avatar } from "@mui/material";
import { MoonIcon } from "@phosphor-icons/react";
import { useTranslations } from "use-intl";

import { standarizedNames } from "@/app/lib/utils/cabinClasses";
import { parseDuration } from "@/app/lib/utils/duration";
import type {
  FindFlightOffersAirlineV2,
  FindFlightOffersFlightSegmentV2,
} from "@/types/codegen";

const AirlineNameAndLogo = ({
  airline,
}: {
  airline: FindFlightOffersAirlineV2;
}) => {
  const t = useTranslations();

  return (
    <div className="flex items-center gap-1">
      <span>{airline.name}</span>
      <Avatar
        alt={t("flights.airlineLogoAlt", {
          airline: airline.name || airline.code,
        })}
        sx={{ width: 16, height: 16 }}
        src={airline.logoUrl}
      />
    </div>
  );
};

const cabinClassLabels: Record<string, string> = {
  [standarizedNames.Economy]: "flights.cabinClass.economy",
  [standarizedNames.PremiumEconomy]: "flights.cabinClass.premiumEconomy",
  [standarizedNames.Business]: "flights.cabinClass.business",
  [standarizedNames.FirstClass]: "flights.cabinClass.firstClass",
};

type Props = {
  segment: FindFlightOffersFlightSegmentV2;
  cabinClasses: string[];
  isOvernight: boolean;
};

export const SegmentDetails = ({
  segment,
  cabinClasses,
  isOvernight,
}: Props) => {
  const t = useTranslations();

  const areCarriersDiffer =
    segment.operatingCarrier.airline.code !==
    segment.marketingCarrier.airline.code;

  // We assume that all travellers has same cabin class
  const cabinClass = cabinClasses[0];

  const segmentDuration = segment.duration
    ? t("durationValue", parseDuration(segment.duration))
    : null;

  return (
    <div className="flex flex-col gap-1 text-sm text-text-primary">
      <AirlineNameAndLogo airline={segment.marketingCarrier.airline} />
      {areCarriersDiffer ? (
        <div className="flex flex-col gap-0">
          <span>{t("flights.operatedBy")}:</span>
          <AirlineNameAndLogo airline={segment.operatingCarrier.airline} />
        </div>
      ) : null}
      <span>
        {segment.marketingCarrier.airline.code}-
        {segment.marketingCarrier.flightNumber}
      </span>
      <span>
        {segment.equipment.name ? `${segment.equipment.name} ` : null}
        {segment.equipment.code}
      </span>
      <span>
        {cabinClassLabels[cabinClass]
          ? t(cabinClassLabels[cabinClass])
          : cabinClass}
      </span>
      <span>{segmentDuration}</span>
      {isOvernight ? (
        <div className="flex items-center gap-0.5 text-semantic-error-strong">
          <span>{t("flights.overnightFlight")}</span>
          <MoonIcon size={16} weight="bold" />
        </div>
      ) : null}
    </div>
  );
};
